import type { Aggregator, Attendant, Customer, CustomerDetails, CustomerHistoryEntry, TicketActivity, TicketDetails, OperatorWork, QueueWait, ReferenceBundle, JourneyTemplate, JourneyTemplateStatus, FieldDef, DaySlot, AvailableDayInfo } from '../domain/types'
import type { DataSource, JourneyFilter, JourneyPage, JourneyCalendarDay, CreateJourneyInput, TransferInput, CallNextResult, CreateTemplateInput, UpdateTemplateInput, OperatorWorkPeriod, UpdateJourneyStepsRequest } from './DataSource'
import { http, ticketing, apiV1, lid, pid } from './http/client'
import { ensurePrevisions, loadPrevisions, loadQueueWaits, validPrevisions, waitingByServiceConfig } from './adapter/scid-resolver'
import * as adapt from './adapter/journey.adapter'
import * as adaptOpWork from './adapter/operatorWork.adapter'
import * as adaptRef from './adapter/reference.adapter'
import { fieldDefs } from './adapter/customField.adapter'
import { adaptTicketDetails } from './adapter/ticketDetails.adapter'
import type { TicketDetailsDto } from './dto/ticketDetails.dto'
import { CUSTOMER_FIELDS } from '../domain/catalog'
import { ApiError } from './errors'
import type { AttendanceJourneyDto, JourneyListItemDto, JourneyListResponse, JourneyTicketDto, OperatorTicketDto, OperatorWorkDto, OperatorWorkCountsDto, CustomerDto, FriendlyCodeDto } from './dto/journey.dto'
import type { EffectiveCustomFieldDto } from './dto/customField.dto'
import type { AttendantDto, AttendantListDto, TicketPrevisionDto, AvailableDaysResponse, AvailableDaysBatchResponse, DaySlotsResponse, JourneyTemplateDto, JourneyTemplateListDto } from './dto/reference.dto'
import type { ServiceListResponse } from './adapter/reference.adapter'
import { digitsOnly } from '../lib/fieldValidation'
import { compactJourneyStepSequences } from '../domain/journeyStepOrder'

const hhmm = (iso?: string): string | null => {
  if (!iso) {return null}
  const d = new Date(iso)
  if (isNaN(d.getTime())) {return null}
  // Converte UTC para UTC-3 (São Paulo)
  d.setHours(d.getHours() - 3)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

// Normaliza o response do callnext, que pode vir em 3 formatos:
// 1. ResponseJson wrapper: { messages: [...], responseData: { id, friendlyCode, status, customerName, ... } }
// 2. OperatorTicketDto enriquecido: { aggregatorId, customer, priority, ticket: { ticketId, friendlyCode, ... } }
// 3. JourneyTicketDto direto: { ticketId, friendlyCode, ... }
type CallNextResponseData = { id: number; friendlyCode?: FriendlyCodeDto; status: string; customerName?: string }
type CallNextResponse = CallNextResponseData | (JourneyTicketDto & { customer?: CustomerDto }) | OperatorTicketDto | { responseData?: CallNextResponseData; messages?: unknown[] } | null | undefined
type BulkTicketActionResponse = { requested?: number; affected?: number; ticketIds?: number[] }
function toCallNextResult(r: CallNextResponse): CallNextResult | null {
  if (!r) {return null}
  // ResponseJson wrapper { responseData: { id, friendlyCode, customerName } }
  if ('responseData' in r && r.responseData) {
    return { ticketId: r.responseData.id, codigoTicket: adapt.friendlyCode(r.responseData.friendlyCode), nome: r.responseData.customerName ?? '' }
  }
  // OperatorTicketDto: { aggregatorId, customer, ticket: { ticketId, friendlyCode } }
  if ('ticket' in r && r.ticket) {
    return { ticketId: r.ticket.ticketId ?? r.ticket.id ?? 0, codigoTicket: adapt.friendlyCode(r.ticket.friendlyCode), nome: (r as OperatorTicketDto).customer?.name ?? '' }
  }
  // JourneyTicketDto direto: { ticketId, friendlyCode }
  if ('ticketId' in r && r.ticketId) {
    return { ticketId: r.ticketId, codigoTicket: adapt.friendlyCode(r.friendlyCode), nome: (r as { customer?: CustomerDto }).customer?.name ?? (r as { resourceName?: string }).resourceName ?? '' }
  }
  // Flat { id, friendlyCode, customerName }
  if ('id' in r && (r as CallNextResponseData).id) {
    return { ticketId: (r as CallNextResponseData).id, codigoTicket: adapt.friendlyCode((r as CallNextResponseData).friendlyCode), nome: (r as CallNextResponseData).customerName ?? '' }
  }
  return null
}

const journeyListItems = (r: JourneyListResponse): JourneyListItemDto[] => {
  if (Array.isArray(r)) {return r}
  return r.items ?? r.data ?? r.result ?? r.results ?? []
}

const customerDtoFromResponse = (value: unknown): CustomerDto | null => {
  if (typeof value === 'number' && value > 0) {return { id: value }}
  if (typeof value === 'string' && /^\d+$/.test(value)) {return { id: Number(value) }}
  if (!value || typeof value !== 'object') {return null}

  const record = value as Record<string, unknown>
  const rawId = record.id ?? record.Id ?? record.clientId ?? record.ClientId
  const id = typeof rawId === 'number' ? rawId : typeof rawId === 'string' && /^\d+$/.test(rawId) ? Number(rawId) : 0
  if (id > 0) {
    return {
      id,
      name: String(record.name ?? record.Name ?? ''),
      email: String(record.email ?? record.Email ?? ''),
      phone: String(record.phone ?? record.Phone ?? ''),
    }
  }

  for (const key of ['responseData', 'ResponseData', 'data', 'Data', 'result', 'Result', 'client', 'Client']) {
    const nested = customerDtoFromResponse(record[key])
    if (nested) {return nested}
  }
  return null
}

interface ManagerCustomerDto {
  id: number
  name?: string
  email?: string
  phone?: string
  providerId?: number
  active?: boolean
  history?: CustomerHistoryEntry[]
}

const managerCustomerDtoFromResponse = (value: unknown): ManagerCustomerDto | null => {
  if (!value || typeof value !== 'object') {return null}
  const record = value as Record<string, unknown>
  const id = Number(record.id ?? record.Id ?? 0)
  if (id > 0) {
    return {
      id,
      name: String(record.name ?? record.Name ?? ''),
      email: String(record.email ?? record.Email ?? ''),
      phone: String(record.phone ?? record.Phone ?? ''),
      providerId: Number(record.providerId ?? record.ProviderId ?? 0),
      active: Boolean(record.active ?? record.Active ?? true),
      history: Array.isArray(record.history ?? record.History)
        ? (record.history ?? record.History) as CustomerHistoryEntry[]
        : [],
    }
  }
  for (const key of ['responseData', 'ResponseData', 'data', 'Data', 'result', 'Result', 'client', 'Client']) {
    const nested = managerCustomerDtoFromResponse(record[key])
    if (nested) {return nested}
  }
  return null
}

const customerDetails = (dto: ManagerCustomerDto): CustomerDetails => ({
  id: dto.id,
  nome: dto.name?.trim() || 'Cliente sem nome',
  cpf: '—',
  telefone: dto.phone ?? '',
  email: dto.email ?? '',
  providerId: dto.providerId ?? 0,
  active: dto.active ?? true,
  history: dto.history ?? [],
})

const attendantItems = (r: AttendantDto[] | AttendantListDto): AttendantDto[] => {
  if (Array.isArray(r)) {return r}
  return r.items ?? r.data ?? r.result ?? r.results ?? []
}

type CustomerSearchResponse = CustomerDto[] | {
  items?: CustomerDto[]
  data?: CustomerDto[]
  result?: CustomerDto[]
  results?: CustomerDto[]
  clients?: CustomerDto[]
}

const customerItems = (r: CustomerSearchResponse): CustomerDto[] => {
  if (Array.isArray(r)) {return r}
  return r.items ?? r.data ?? r.result ?? r.results ?? r.clients ?? []
}

const isNumericId = (id: unknown): boolean => typeof id === 'number' || (typeof id === 'string' && /^\d+$/.test(id))

const attendant = (a: AttendantDto): Attendant | null => {
  const candidates = [a.resourceId, a.ResourceId, a.id, a.Id, a.userId, a.UserId]
  const id = candidates.find(isNumericId) ?? candidates.find((v) => v != null && v !== '')
  if (id == null || id === '') {return null}
  return {
    id: String(id),
    nome: a.name ?? a.Name ?? a.resourceName ?? a.ResourceName ?? a.fullName ?? a.FullName ?? a.displayName ?? a.DisplayName ?? 'Atendente',
  }
}

const normalizeOperatorWorkCounts = (input: OperatorWorkCountsDto | OperatorWorkCountsDto[]): Pick<OperatorWork, 'eligCountByServiceConfig' | 'eligCountBySession' | 'otherCountByServiceConfig'> => {
  const raw = Array.isArray(input)
    ? { items: input }
    : Array.isArray(input.responseData) || (input.responseData && typeof input.responseData === 'object')
      ? input.responseData
      : Array.isArray(input.data) || (input.data && typeof input.data === 'object')
        ? input.data
        : Array.isArray(input.result) || (input.result && typeof input.result === 'object')
          ? input.result
          : input
  if (Array.isArray(raw)) {return normalizeOperatorWorkCounts({ items: raw })}
  const dto = raw as OperatorWorkCountsDto

  const toCount = (value: unknown): number | null => {
    const n = Number(value)
    return Number.isFinite(n) ? n : null
  }
  const countFrom = (record: Record<string, unknown>): number | null =>
    toCount(record.eligible ?? record.eligibleCount ?? record.count ?? record.waitingCount ?? record.ticketsWaiting ?? record.total)
  const otherCountFrom = (record: Record<string, unknown>, eligible: number | null): number | null => {
    const explicit = toCount(record.other ?? record.otherCount ?? record.nonEligible ?? record.nonEligibleCount ?? record.active ?? record.activeCount)
    if (explicit != null) {return explicit}
    const total = toCount(record.total)
    if (total != null && eligible != null) {return Math.max(0, total - eligible)}
    const serving = toCount(record.serving) ?? 0
    const blocked = toCount(record.blocked) ?? 0
    const expired = toCount(record.expired) ?? 0
    const sum = serving + blocked + expired
    return sum > 0 ? sum : null
  }
  const idFrom = (record: Record<string, unknown>): number | null =>
    toCount(record.serviceConfigId ?? record.id)
  const sessionIdFrom = (record: Record<string, unknown>): number | null =>
    toCount(record.sessionId ?? record.id)
  const mergeSessionCounts = (serviceConfigId: number, value: unknown, target: Record<number, Record<number, number>>): void => {
    if (!value) {return}
    const bySession = (target[serviceConfigId] ||= {})
    if (Array.isArray(value)) {
      for (const item of value) {
        if (!item || typeof item !== 'object') {continue}
        const record = item as Record<string, unknown>
        const sessionId = sessionIdFrom(record)
        const count = countFrom(record)
        if (sessionId != null && count != null) {bySession[sessionId] = count}
      }
      return
    }
    if (typeof value === 'object') {
      for (const [sessionId, count] of Object.entries(value as Record<string, unknown>)) {
        const parsedCount = toCount(count)
        if (parsedCount != null) {bySession[Number(sessionId)] = parsedCount}
      }
    }
  }

  const eligCountBySession: Record<number, Record<number, number>> = {
    ...(dto.eligCountBySession ??
    dto.countsBySession ??
    dto.sessionCounts ??
    {}),
  }
  const eligCountByServiceConfig: Record<number, number> = {
    ...(typeof dto.counts === 'object' && !Array.isArray(dto.counts) ? dto.counts : {}),
    ...(dto.eligCountByServiceConfig ??
    dto.countsByServiceConfig ??
    dto.serviceConfigCounts ??
    {}),
  }
  const otherCountByServiceConfig: Record<number, number> = {
    ...(dto.otherCountByServiceConfig ?? {}),
  }

  const items = [
    ...(dto.items ?? []),
    ...(dto.serviceConfigs ?? []),
    ...(Array.isArray(dto.counts) ? dto.counts : []),
  ]
  for (const item of items) {
    const record = item as Record<string, unknown>
    const serviceConfigId = idFrom(record)
    if (serviceConfigId == null) {continue}
    const count = countFrom(record)
    if (count != null) {eligCountByServiceConfig[serviceConfigId] = count}
    const otherCount = otherCountFrom(record, count)
    if (otherCount != null) {otherCountByServiceConfig[serviceConfigId] = otherCount}
    mergeSessionCounts(serviceConfigId, record.sessions, eligCountBySession)
    mergeSessionCounts(serviceConfigId, record.sessionCounts, eligCountBySession)
    mergeSessionCounts(serviceConfigId, record.countsBySession, eligCountBySession)
    mergeSessionCounts(serviceConfigId, record.eligCountBySession, eligCountBySession)
    mergeSessionCounts(serviceConfigId, record.eligibleBySession, eligCountBySession)
  }

  if (Object.keys(eligCountByServiceConfig).length === 0) {
    Object.assign(eligCountByServiceConfig, Object.fromEntries(
      Object.entries(eligCountBySession).map(([serviceConfigId, bySession]) => [
        Number(serviceConfigId),
        Object.values(bySession ?? {}).reduce((sum, value) => sum + value, 0),
      ]),
    ) as Record<number, number>)
  }

  return { eligCountByServiceConfig, eligCountBySession, otherCountByServiceConfig }
}

const resourcePayloadId = (resourceId: string): number => {
  if (/^\d+$/.test(resourceId)) {return Number(resourceId)}
  throw new ApiError(400, 'INVALID_RESOURCE_ID', 'Atendente inválido. Selecione novamente o atendente da etapa.')
}

const idempotencyKey = (): string => {
  const randomUUID = globalThis.crypto?.randomUUID
  if (randomUUID) {return randomUUID.call(globalThis.crypto)}
  return `journey-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const ifMatch = (etag: string): Record<string, string> | undefined =>
  etag.trim() ? { 'If-Match': etag } : undefined

// RealDataSource — consome os endpoints reais (ticketing v2 + monolito), traduzindo
// na borda via adapters. M2 = LEITURAS; as ações (escrita) entram no M3.
// Requer backend acessível + token + provider/location (env). Sem isso, falha explícita;
// o mock continua sendo o default offline.
export class RealDataSource implements DataSource {
  // ---- leitura ----
  async listJourneys(filter?: JourneyFilter, signal?: AbortSignal): Promise<Aggregator[]> {
    const list = await http.get<JourneyListResponse>('v2', ticketing(`locations/${lid()}/journeys`), {
      status: filter?.status, q: filter?.q, dateFilter: filter?.dateFilter, pageSize: 500,
    }, { signal })
    return journeyListItems(list).map(adapt.listItem)
  }

  async listJourneyPage(filter: JourneyFilter, signal?: AbortSignal): Promise<JourneyPage> {
    const response = await http.get<JourneyListResponse>('v2', ticketing(`locations/${lid()}/journeys`), {
      ...filter, pageSize: filter.pageSize ?? 15,
      dateFilter: filter.sessionFrom || filter.sessionTo ? 'all' : filter.dateFilter,
    }, { signal })
    if (Array.isArray(response) || !response.counters) {
      throw new ApiError(502, 'JOURNEY_LIST_CONTRACT', 'Não foi possível carregar a página de atendimentos. Tente novamente.')
    }
    return {
      items: journeyListItems(response).map(adapt.listItem),
      pageSize: response.pageSize ?? filter.pageSize ?? 15,
      hasMore: response.hasMore ?? false,
      nextCursor: response.nextCursor,
      counters: response.counters,
    }
  }

  async getJourneyCalendar(month: string, filter: JourneyFilter = {}, signal?: AbortSignal): Promise<JourneyCalendarDay[]> {
    const { cursor: _cursor, pageSize: _size, dateFilter: _date, ...criteria } = filter
    return http.get<JourneyCalendarDay[]>('v2', ticketing(`locations/${lid()}/journeys/calendar`), {
      ...criteria, month: `${month.slice(0, 7)}-01`,
    }, { signal })
  }

  async getJourney(aggregatorId: string): Promise<Aggregator | undefined> {
    await ensurePrevisions() // resolve sala/code por serviceConfigId
    const dto = await http.get<AttendanceJourneyDto>('v2', ticketing(`journeys/${aggregatorId}`))
    return dto ? adapt.journey(dto) : undefined
  }

  async getOperatorWork(serviceConfigId: number, sessionId: number | null, period?: OperatorWorkPeriod | null): Promise<OperatorWork> {
    await ensurePrevisions() // p/ eligCount por fila (ticketsWaiting) + sala/code no adapt.ticket
    const dto = await http.get<OperatorWorkDto>('v2', ticketing(`locations/${lid()}/serviceConfigs/${serviceConfigId}/operator-work`), {
      ...(sessionId != null ? { sessionId: String(sessionId) } : {}),
      ...(period ? { period } : {}),
      limit: 200,
    })
    const work = adaptOpWork.operatorWork(dto)
    const eligCountByServiceConfig = Object.keys(dto.eligCountBySession ?? {}).length
      ? Object.fromEntries(
        Object.entries(dto.eligCountBySession ?? {}).map(([serviceConfigId, bySession]) => [
          Number(serviceConfigId),
          Object.values(bySession ?? {}).reduce((sum, value) => sum + value, 0),
        ]),
      ) as Record<number, number>
      : waitingByServiceConfig()
    return { ...work, eligCountByServiceConfig }
  }

  // Reference data real: filas ← serviceConfigs/previsions. Disponibilidade e slots são lazy
  // na criação para evitar fan-out de available-days no boot do painel operacional.
  async getReferenceData(): Promise<ReferenceBundle> {
    const previsions = validPrevisions(await loadPrevisions())
    const [templates, services, allFields] = await Promise.all([
      http.get<JourneyTemplateDto[]>('v2', ticketing(`locations/${lid()}/journey-templates?status=Active`)).catch(() => [] as JourneyTemplateDto[]),
      http.get<ServiceListResponse>('v1', apiV1(`locations/${lid()}/services/with-queues`)).catch(() => [] as ServiceListResponse),
      this.getJourneyFields(null, null).catch(() => [] as FieldDef[]),
    ])

    const nativeCustomerFields = CUSTOMER_FIELDS.filter((c) => ['nome', 'telefone', 'email'].includes(c.key))
    const backendCfs = allFields.filter((x) => x.anchor === 'Customer')
    const backendJfs = allFields.filter((x) => x.anchor === 'Journey')

    return { 
      ...adaptRef.referenceBundle(previsions, [], templates, services), 
      customerFields: [...nativeCustomerFields, ...backendCfs], 
      journeyFields: backendJfs 
    }
  }

  async getQueueWaits(): Promise<QueueWait[]> {
    return loadQueueWaits()
  }

  // Campos customizados efetivos da criação (OnCreate). O backend resolve por templateId (int) +
  // customerId — escopos provider/location/JourneyTemplate/customer. templateId null = sem modelo.
  async getJourneyFields(templateId: number | null, customerId: number | null, trigger?: string): Promise<FieldDef[]> {
    const list = await http.get<EffectiveCustomFieldDto[]>('v2', ticketing(`locations/${lid()}/journey-fields`), { templateId: templateId ?? undefined, customerId: customerId ?? undefined, trigger })
    return fieldDefs(list)
  }

  async getQueueFields(sessionId: number): Promise<FieldDef[]> {
    const fields = await http.get<{ id: number; title: string; type: number; required: boolean; maxLength?: number; order?: number }[]>('v1', apiV1(`sessions/${sessionId}/custom-fields`))
    return Promise.all(fields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map(async (field): Promise<FieldDef> => ({
      key: `customField:${field.id}`,
      label: field.title,
      type: field.type === 1 ? 'number' : field.type === 2 ? 'date' : field.type === 3 ? 'cpf' : field.type === 4 ? 'select' : field.type === 5 ? 'cnpj' : 'text',
      required: field.required,
      maxLength: field.maxLength,
      options: field.type === 4
        ? (await http.get<{ name: string; value: string }[]>('v1', apiV1(`locations/${lid()}/custom-fields/${field.id}/options`))).map((option) => ({ label: option.name, value: option.value }))
        : undefined,
    })))
  }

  async getTransitionFields(ticketId: number, trigger: string): Promise<FieldDef[]> {
    const list = await http.get<EffectiveCustomFieldDto[]>('v2', ticketing(`tickets/${ticketId}/transition-fields`), { trigger })
    return fieldDefs(list)
  }

  async getAttendants(): Promise<Attendant[]> {
    const list = await http.get<AttendantDto[] | AttendantListDto>('v2', `manager/providers/${pid()}/locations/${lid()}/attendants`)
    return attendantItems(list).map(attendant).filter((a): a is Attendant => a != null)
  }

  async getOperatorWorkCounts(sessionId: number | null): Promise<Pick<OperatorWork, 'eligCountByServiceConfig' | 'eligCountBySession' | 'otherCountByServiceConfig'>> {
    const dto = await http.get<OperatorWorkCountsDto>('v2', ticketing(`locations/${lid()}/operator-work/counts`), {
      ...(sessionId != null ? { sessionId: String(sessionId) } : {}),
    })
    return normalizeOperatorWorkCounts(dto)
  }

  async searchCustomers(q: string, offset = 0): Promise<Customer[]> {
    if (!q.trim()) {return []}
    const response = await http.get<CustomerSearchResponse>('v2', `manager/providers/${pid()}/clients/search`, { searchString: q, limit: 10, offset })
    return customerItems(response).map((c) => ({ id: c.id ?? 0, nome: c.name ?? 'Cliente sem nome', cpf: c.cpf ?? '—', telefone: c.phone ?? '—', email: c.email ?? '' }))
  }

  async getCustomer(customerId: number): Promise<CustomerDetails> {
    const response = await http.get<unknown>('v2', `manager/providers/${pid()}/clients/${customerId}`)
    const dto = managerCustomerDtoFromResponse(response)
    if (!dto) {throw new ApiError(502, 'CUSTOMER_INVALID_RESPONSE', 'Não foi possível carregar os dados do cliente.')}
    return customerDetails(dto)
  }

  async updateCustomer(customer: CustomerDetails): Promise<CustomerDetails> {
    const payload: ManagerCustomerDto = {
      id: customer.id,
      name: customer.nome.trim(),
      email: customer.email.trim(),
      phone: digitsOnly(customer.telefone),
      providerId: customer.providerId,
      active: customer.active,
      history: customer.history,
    }
    await http.put<unknown>('v2', `manager/providers/${pid()}/clients/${customer.id}`, payload)
    return customerDetails(payload)
  }

  async createCustomer(customer: Customer): Promise<Customer> {
    const phone = digitsOnly(customer.telefone)
    const exactMatch = (item: Customer): boolean =>
      (!!customer.email && item.email.toLowerCase() === customer.email.toLowerCase()) ||
      (!!phone && digitsOnly(item.telefone) === phone)
    const existing = (await this.searchCustomers(customer.email || phone || customer.nome)).find(exactMatch)
    if (existing?.id) {return { ...existing, cpf: customer.cpf, customData: customer.customData }}

    const response = await http.post<unknown>(
      'v2',
      `manager/providers/${pid()}/clients`,
      { name: customer.nome, email: customer.email, phone },
    )
    let dto = customerDtoFromResponse(response)
    for (let attempt = 0; !dto?.id && attempt < 3; attempt += 1) {
      if (attempt > 0) {await new Promise((resolve) => setTimeout(resolve, 250))}
      const created = (await this.searchCustomers(customer.email || phone || customer.nome)).find(exactMatch)
      if (created?.id) {dto = { id: created.id, name: created.nome, email: created.email, phone: created.telefone }}
    }
    if (!dto?.id) {throw new ApiError(502, 'CUSTOMER_WITHOUT_ID', 'Cliente cadastrado, mas não foi possível localizar o id retornado.')}
    return {
      id: dto.id,
      // O manager pode confirmar a criação retornando somente o id. Nesse caso,
      // os campos normalizados no DTO ficam como string vazia; preserve os dados
      // digitados para que a jornada receba nome e contato junto com o novo id.
      nome: dto.name?.trim() || customer.nome,
      cpf: customer.cpf,
      telefone: dto.phone?.trim() || customer.telefone,
      email: dto.email?.trim() || customer.email,
      customData: customer.customData,
    }
  }

  async getTicketPrevision(ticketId: number): Promise<string | null> {
    const p = await http.get<TicketPrevisionDto>('v2', `prevision/tickets/${ticketId}`)
    return p.estimatedTime ?? hhmm(p.estimatedServiceStart)
  }

  async getTicketHistory(ticketId: number): Promise<TicketActivity[]> {
    return (await this.getTicketDetails(ticketId)).activities
  }

  async getTicketDetails(ticketId: number): Promise<TicketDetails> {
    const res = await http.get<TicketDetailsDto>('v2', ticketing(`tickets/${ticketId}`))
    return adaptTicketDetails(res)
  }

  // ---- ações (M3) ----
  // Transições de ticket passam pelo GATE de jornada no gateway (roda a política de
  // nível ANTES da transição); conflito ONE_ACTIVE_PER_LEVEL volta HTTP 409 e o
  // client o converte em ConflictError. Não há motor local — a verdade é do servidor.

  async createJourney(input: CreateJourneyInput): Promise<Aggregator> {
    await ensurePrevisions() // resolve sala/code por serviceConfigId p/ o adapter
    const dto = await http.post<AttendanceJourneyDto>('v2', ticketing(`locations/${lid()}/journeys`), {
      // id 0/negativo → backend trata como CustomerId = null (jornada anônima,
      // dados copiados denormalizados nos bilhetes — padrão autoatendimento).
      customer: {
        id: input.cliente.id,
        name: input.cliente.nome || 'AUTOATENDIMENTO',
        ...(input.cliente.cpf && input.cliente.cpf !== '—' ? { document: input.cliente.cpf } : {}),
        phone: digitsOnly(input.cliente.telefone),
        email: input.cliente.email,
      },
      priority: input.priority,
      autoConfirm: input.scheduledDate == null,
      ...(input.scheduledDate != null ? { scheduledDate: input.scheduledDate } : {}),
      attendanceType: 'PRESENTIAL',
      ...(input.templateId != null ? { templateId: input.templateId } : {}),
      metadata: { ...input.metadata, ...(input.cliente.customData || {}) },
      steps: compactJourneyStepSequences([...input.itens].sort((a, b) => a.sequence - b.sequence)).map((it, i) => ({
        serviceId: it.serviceId,
        serviceConfigId: it.serviceConfigId,
        sessionId: it.sessionId,
        sequence: it.sequence + 1,
        sortOrder: i+1,
        required: true,
        ...(input.scheduledDate != null ? { resourceId: it.resourceId, bookedStartTime: it.bookedStartTime } : {}),
        ...(it.customFields ? { customFields: it.customFields } : {}),
      })),
    }, { 'Idempotency-Key': idempotencyKey() })
    return adapt.journey(dto)
  }

  async updateJourneySteps(aggregatorId: string, request: UpdateJourneyStepsRequest): Promise<Aggregator> {
    const dto = await http.patch<AttendanceJourneyDto>('v2', ticketing(`journeys/${aggregatorId}/steps`), {
      operations: request.operations.map((operation) => ({ ...operation })),
    })
    return adapt.journey(dto)
  }
  async addJourneyStep(aggregatorId: string, input: { serviceId: number; serviceConfigId: number; sessionId: number; required?: boolean; allowSkip?: boolean; estimatedDurationMinutes?: number }): Promise<Aggregator> {
    const dto = await http.post<AttendanceJourneyDto>('v2', ticketing(`locations/${lid()}/journeys/${aggregatorId}/tickets`), {
      steps: [{
        serviceId: input.serviceId,
        serviceConfigId: input.serviceConfigId,
        sessionId: input.sessionId,
        ...(input.required != null ? { required: input.required } : {}),
        ...(input.allowSkip != null ? { allowSkip: input.allowSkip } : {}),
        ...(input.estimatedDurationMinutes != null ? { estimatedDurationMinutes: input.estimatedDurationMinutes } : {}),
      }],
    })
    return adapt.journey(dto)
  }

  async updateJourneyCustomer(aggregatorId: string, customerId: number): Promise<Aggregator> {
    const dto = await http.put<AttendanceJourneyDto>('v2', ticketing(`journeys/${aggregatorId}/customer`), {
      customerId,
    })
    return adapt.journey(dto)
  }


  // TODO(journey/perf): resposta hoje é só { code: 'JOURNEY_CANCELED_SUCCESS' } (JourneysController.
  // Cancel, filazero-api-ticketing) — sem o Aggregator atualizado. App.tsx precisa de reload()/
  // getJourney() depois desta chamada. Ver DataSource.ts para o plano de quando o backend evoluir.
  async cancelJourney(aggregatorId: string, reason: string, fields?: Record<string, string>): Promise<void> {
    const body = fields && Object.keys(fields).length ? { reason, customFields: fields } : { reason }
    await http.post('v2', ticketing(`journeys/${aggregatorId}/cancel`), body)
  }

  // Cancela UM bilhete (não a jornada). Mesmo gate OnCancel do backend (ServiceCancellation).
  // TODO(journey/perf): resposta hoje é só CancelTicketsJson ({ code, message }) — sem o ticket
  // atualizado (TicketsController.Cancel, filazero-api-ticketing). Mesma limitação do cancelJourney acima.
  async cancelTicket(ticketId: number, reason: string, fields?: Record<string, string>): Promise<void> {
    const body = fields && Object.keys(fields).length ? { reason, customFields: fields } : { reason }
    await http.post('v2', ticketing(`tickets/${ticketId}/cancel`), body)
  }

  async call(ticketId: number, resourceId: string, _opts?: { recall?: boolean }): Promise<void> {
    // recall (re-chamar) reusa a mesma rota — não há endpoint dedicado; o backend
    // reincrementa timesCalled/lastTimeCalled. O gate de nível pode devolver 409.
    await http.post('v2', ticketing(`tickets/${ticketId}/call`), { resourceId: resourcePayloadId(resourceId) })
  }

  async callNext(serviceConfigId: number, resourceId: string, opts?: { priorityOnly?: boolean; sessionId?: number }): Promise<CallNextResult | null> {
    const body: Record<string, unknown> = {
      resourceId: resourcePayloadId(resourceId),
      ...(opts?.priorityOnly != null ? { callSpecial: opts.priorityOnly } : {}),
      ...(opts?.sessionId != null ? { sessionId: opts.sessionId } : {}),
    }
    const r = await http.post<CallNextResponse>('v2', ticketing(`serviceConfigs/${serviceConfigId}/callnext`), body)
    return toCallNextResult(r)
  }

  async start(ticketId: number, resourceId: string): Promise<void> {
    await http.post('v2', ticketing(`tickets/${ticketId}/start`), { resourceId: resourcePayloadId(resourceId) })
  }

  // complete/confirm/check/reopen: o ticket já carrega seu resource (atribuído no call/start)
  // e a interface não o expõe aqui → não reenviamos resourceId; o backend resolve pelo ticket.
  // TODO(journey/perf): resposta hoje é só CompleteTicketJson ({ Id: ticketId } + mensagem) — sem
  // o ticket atualizado (TicketsController.Complete, filazero-api-ticketing). Mesma limitação
  // documentada em DataSource.ts; App.tsx compensa com reload()/getJourney() após o POST.
  async complete(ticketId: number, fields?: Record<string, string>): Promise<void> {
    const body = fields && Object.keys(fields).length ? { customFields: fields } : {}
    await http.post('v2', ticketing(`tickets/${ticketId}/complete`), body)
  }
  async confirmJourney(aggregatorId: string): Promise<Aggregator> {
    const dto = await http.post<AttendanceJourneyDto>('v2', ticketing(`journeys/${aggregatorId}/confirm`))
    return adapt.journey(dto)
  }
  async checkJourney(aggregatorId: string): Promise<Aggregator> {
    const dto = await http.post<AttendanceJourneyDto>('v2', ticketing(`journeys/${aggregatorId}/check`))
    return adapt.journey(dto)
  }
  async confirmTickets(ticketIds: number[]): Promise<void> {
    await http.post<BulkTicketActionResponse>('v2', ticketing('tickets/confirm'), { ticketIds })
  }
  async checkTickets(ticketIds: number[]): Promise<void> {
    await http.post<BulkTicketActionResponse>('v2', ticketing('tickets/check'), { ticketIds })
  }
  // TODO(journey/perf): confirm/check devolvem só ConfirmTicketJson/CheckinTicketJson
  // ({ Id: ticketId } + mensagem) — mesma limitação do complete acima.
  async confirm(ticketId: number, fields?: Record<string, string>): Promise<void> {
    const body = fields && Object.keys(fields).length ? { customFields: fields } : {}
    await http.post('v2', ticketing(`tickets/${ticketId}/confirm`), body)
  }
  async check(ticketId: number, fields?: Record<string, string>): Promise<void> {
    const body = fields && Object.keys(fields).length ? { customFields: fields } : {}
    await http.post('v2', ticketing(`tickets/${ticketId}/check`), body)
  }
  // TODO(journey/perf): reopen devolve só ReOpenTicketJson ({ Id: ticketId } + mensagem) —
  // mesma limitação do complete acima.
  async reopen(ticketId: number): Promise<void> {
    await http.post('v2', ticketing(`tickets/${ticketId}/reopen`))
  }
  // Reinserir no fim da fila — mesma rota v2 do filazero-app (action=reinsert).
  async reinsert(ticketId: number): Promise<void> {
    await http.post('v2', ticketing(`tickets/${ticketId}/reinsert`))
  }

  async transfer(ticketId: number, input: TransferInput): Promise<void> {
    // rota de transferência (ticketing v2, por location). customer/customData são
    // resolvidos pelo backend a partir do ticket de origem.
    await http.post('v2', ticketing(`locations/${lid()}/tickets/${ticketId}/transfer`), {
      serviceId: input.serviceId,
      serviceConfigId: input.serviceConfigId,
      sessionId: input.sessionId,
      attendanceType: 'PRESENTIAL',
      ...(input.priority !== undefined ? { priority: input.priority } : {}),
      ...(input.customFields && Object.keys(input.customFields).length ? { customFields: input.customFields } : {}),
    })
  }

  async reschedule(ticketId: number, sessionId: number): Promise<void> {
    await http.post('v2', ticketing(`tickets/${ticketId}/reschedule`), { sessionId })
  }

  // ---- templates ----
  async listTemplates(status?: JourneyTemplateStatus): Promise<JourneyTemplate[]> {
    const query: Record<string, unknown> = { pageSize: 200 }
    if (status) {query.status = status}
    const r = await http.get<JourneyTemplateListDto | JourneyTemplateDto[]>('v2', ticketing(`locations/${lid()}/journey-templates`), query)
    const items = Array.isArray(r) ? r : (r as JourneyTemplateListDto).items ?? []
    return items.map(adaptRef.adaptTemplateDto)
  }

  async getTemplate(id: number): Promise<JourneyTemplate> {
    const dto = await http.get<JourneyTemplateDto>('v2', ticketing(`locations/${lid()}/journey-templates/${id}`))
    return adaptRef.adaptTemplateDto(dto)
  }

  async createTemplate(input: CreateTemplateInput): Promise<JourneyTemplate> {
    const dto = await http.post<JourneyTemplateDto>('v2', ticketing(`locations/${lid()}/journey-templates`), {
      name: input.name,
      description: input.description,
      sameLevelPolicy: input.sameLevelPolicy === 'AllowConcurrentOnLevel' ? 2 : 1,
      steps: input.steps.map((s, i) => ({
        clientStepId: s.clientStepId,
        serviceId: s.serviceId,
        serviceConfigId: s.serviceConfigId,
        sessionId: s.sessionId,
        sequence: s.sequence ?? i + 1,
        sortOrder: s.sortOrder ?? i + 1,
        required: s.required,
        allowSkip: s.allowSkip,
        estimatedDurationMinutes: s.estimatedDurationMinutes,
      })),
    })
    return adaptRef.adaptTemplateDto(dto)
  }

  async updateTemplate(id: number, input: UpdateTemplateInput, etag: string): Promise<JourneyTemplate> {
    const dto = await http.put<JourneyTemplateDto>('v2', ticketing(`locations/${lid()}/journey-templates/${id}`), {
      name: input.name,
      description: input.description,
      sameLevelPolicy: input.sameLevelPolicy === 'AllowConcurrentOnLevel' ? 2 : 1,
      steps: input.steps.map((s, i) => ({
        clientStepId: s.clientStepId,
        serviceId: s.serviceId,
        serviceConfigId: s.serviceConfigId,
        sessionId: s.sessionId,
        sequence: s.sequence ?? i + 1,
        sortOrder: s.sortOrder ?? i + 1,
        required: s.required,
        allowSkip: s.allowSkip,
        estimatedDurationMinutes: s.estimatedDurationMinutes,
      })),
    }, ifMatch(etag))
    return adaptRef.adaptTemplateDto(dto)
  }

  async activateTemplate(id: number, etag: string): Promise<JourneyTemplate> {
    const dto = await http.post<JourneyTemplateDto>('v2', ticketing(`locations/${lid()}/journey-templates/${id}/activate`), {}, ifMatch(etag))
    return adaptRef.adaptTemplateDto(dto)
  }

  async cloneTemplate(id: number): Promise<JourneyTemplate> {
    const dto = await http.post<JourneyTemplateDto>('v2', ticketing(`locations/${lid()}/journey-templates/${id}/clone`), {})
    return adaptRef.adaptTemplateDto(dto)
  }

  async archiveTemplate(id: number, etag: string): Promise<void> {
    await http.post<void>('v2', ticketing(`locations/${lid()}/journey-templates/${id}/archive`), {}, ifMatch(etag))
  }

  async unarchiveTemplate(id: number, etag: string): Promise<JourneyTemplate> {
    const dto = await http.post<JourneyTemplateDto>('v2', ticketing(`locations/${lid()}/journey-templates/${id}/unarchive`), {}, ifMatch(etag))
    return adaptRef.adaptTemplateDto(dto)
  }

  async changePriority(aggregatorId: string, priority: number): Promise<void> {
    await http.post('v2', ticketing(`journeys/${aggregatorId}/priority`), { priority })
  }

  // — custom fields (admin) —
  async listCustomFields(): Promise<import('../domain/types').CustomFieldDto[]> {
    return http.get<import('../domain/types').CustomFieldDto[]>('v2', ticketing('customfields'))
  }

  async getCustomField(id: number): Promise<import('../domain/types').CustomFieldDto> {
    return http.get<import('../domain/types').CustomFieldDto>('v2', ticketing(`customfields/${id}`))
  }

  async createCustomField(input: import('../domain/types').CreateCustomFieldDto): Promise<import('../domain/types').CustomFieldDto> {
    return http.post<import('../domain/types').CustomFieldDto>('v2', ticketing('customfields'), input)
  }

  async updateCustomField(id: number, input: import('../domain/types').UpdateCustomFieldDto): Promise<import('../domain/types').CustomFieldDto> {
    return http.put<import('../domain/types').CustomFieldDto>('v2', ticketing(`customfields/${id}`), input)
  }

  async deleteCustomField(id: number): Promise<void> {
    await http.delete('v2', ticketing(`customfields/${id}`))
  }

  async getAvailableDaysBatch(serviceConfigIds: number[]): Promise<AvailableDayInfo[]> {
    const ids = [...new Set(serviceConfigIds)]
    if (ids.length === 0) {return []}
    const r = await http.post<AvailableDaysBatchResponse[]>('v2', ticketing(`locations/${lid()}/serviceConfigs/available-days-batch`), { serviceConfigIds: ids, days: 7 })
    return r.flatMap((x) => (x.availableDays ?? []).map((day) => adaptRef.adaptAvailableDay(x.serviceConfigId, day)))
  }

  async getDaySlots(serviceConfigId: number, date: string): Promise<DaySlot[]> {
    const r = await http.get<DaySlotsResponse>('v2', ticketing(`locations/${lid()}/serviceConfigs/${serviceConfigId}/day-slots`), { date })
    return r.slots.map(adaptRef.adaptSlot)
  }

  async getAvailableDays(serviceConfigId: number): Promise<AvailableDayInfo[]> {
    // sem startDate: backend resolve "hoje" no fuso do provider (toISOString() aqui é UTC e pulava o dia corrente à noite)
    const r = await http.get<AvailableDaysResponse>('v2', ticketing(`locations/${lid()}/serviceConfigs/${serviceConfigId}/available-days`), { days: 7 })
    return r.availableDays.map((day) => adaptRef.adaptAvailableDay(r.serviceConfigId, day))
  }
}
